import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "src/lib/utils";

interface BreadcrumbItem {
	label: string;
	path?: string;
}

const routeBreadcrumbs: Record<string, BreadcrumbItem[]> = {
	// List pages
	"/vendor-sponsorship/homepage-carousel": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Carousel" }
	],
	"/vendor-sponsorship/full-screen-takeover": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Takeover" }
	],
	"/vendor-sponsorship/promotion": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Promotions" }
	],
	"/vendor-sponsorship/app-branding": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Branding" }
	],
	"/social-feed": [{ label: "App" }, { label: "Social Feed" }],
	"/analytics": [{ label: "Analytics" }],
	"/announcements": [{ label: "Announcements" }],
	"/announcements/create": [
		{
			label: "Announcements",
			path: "/announcements"
		},
		{ label: "New Announcement" }
	],
	"/announcements/": [
		{
			label: "Announcements",
			path: "/announcements"
		},
		{ label: "Edit Announcement" }
	],
	"/social-feed/hashtags": [
		{ label: "App" },
		{ label: "Social Feed" },
		{ label: "Hashtags" }
	],
	"/social-feed/hashtags/create": [
		{ label: "App" },
		{
			label: "Hashtags",
			path: "/social-feed/hashtags"
		},
		{ label: "New Hashtag" }
	],
	"/social-feed/hashtags/": [
		{ label: "App" },
		{
			label: "Hashtags",
			path: "/social-feed/hashtags"
		},
		{ label: "Edit Hashtag" }
	],
	"/social-feed/favourite-feeds": [
		{ label: "App" },
		{ label: "Social Feed" },
		{ label: "Favourites" }
	],
	"/app/events": [{ label: "App" }, { label: "Events" }],
	"/app/events/create": [
		{ label: "App" },
		{
			label: "Events",
			path: "/app/events"
		},
		{ label: "New Event" }
	],
	"/app/events/": [
		{ label: "App" },
		{
			label: "Events",
			path: "/app/events"
		},
		{ label: "Edit Event" }
	],
	"/app/homepage/quick-links": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Quick Links" }
	],
	"/app/homepage/quick-links/create": [
		{ label: "App" },
		{
			label: "Quick Links",
			path: "/app/homepage/quick-links"
		},
		{ label: "New Quick Link" }
	],
	"/observations/submissions": [
		{ label: "Observations" },
		{ label: "Submissions" }
	],
	"/observations/forms": [{ label: "Observations" }, { label: "Forms" }],
	"/observations/reports": [{ label: "Observations" }, { label: "Reports" }],
	// Create/Edit pages
	"/vendor-sponsorship/homepage-carousel/banners/new": [
		{ label: "App" },
		{
			label: "Carousel",
			path: "/vendor-sponsorship/homepage-carousel/banners?published=true"
		},
		{ label: "New Banner" }
	],
	"/vendor-sponsorship/homepage-carousel/banners/edit": [
		{ label: "App" },
		{
			label: "Carousel",
			path: "/vendor-sponsorship/homepage-carousel/banners?published=true"
		},
		{ label: "Edit Banner" }
	],
	"/vendor-sponsorship/full-screen-takeover/banners/new": [
		{ label: "App" },
		{
			label: "Takeover",
			path: "/vendor-sponsorship/full-screen-takeover/banners?published=true"
		},
		{ label: "New Takeover" }
	],
	"/vendor-sponsorship/full-screen-takeover/banners/edit": [
		{ label: "App" },
		{
			label: "Takeover",
			path: "/vendor-sponsorship/full-screen-takeover/banners?published=true"
		},
		{ label: "Edit Takeover" }
	],
	"/vendor-sponsorship/promotion/banners/new": [
		{ label: "App" },
		{
			label: "Promotions",
			path: "/vendor-sponsorship/promotion/banners?published=true"
		},
		{ label: "New Promotion" }
	],
	"/vendor-sponsorship/promotion/banners/edit": [
		{ label: "App" },
		{
			label: "Promotions",
			path: "/vendor-sponsorship/promotion/banners?published=true"
		},
		{ label: "Edit Promotion" }
	],
	"/vendor-sponsorship/app-branding/brands/new-app-branding-form": [
		{ label: "App" },
		{
			label: "Branding",
			path: "/vendor-sponsorship/app-branding/brands?published=true"
		},
		{ label: "New Brand" }
	],
	"/vendor-sponsorship/app-branding/brands/edit": [
		{ label: "App" },
		{
			label: "Branding",
			path: "/vendor-sponsorship/app-branding/brands?published=true"
		},
		{ label: "Edit Brand" }
	],
	"/app/homepage/quick-links/": [
		{ label: "App" },
		{
			label: "Quick Links",
			path: "/app/homepage/quick-links"
		},
		{ label: "Edit Quick Link" }
	],
	"/app/homepage/key-shouts": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Key Shouts" }
	],
	"/app/homepage/ordering": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Ordering" }
	],
	"/app/homepage/section-titles": [
		{ label: "App" },
		{ label: "Homepage" },
		{ label: "Section Titles" }
	],
	"/app/navigation": [{ label: "App" }, { label: "Navigation" }],
	"/app/homepage/key-shouts/create": [
		{ label: "App" },
		{
			label: "Key Shouts",
			path: "/app/homepage/key-shouts"
		},
		{ label: "New Key Shout" }
	],
	"/app/homepage/key-shouts/": [
		{ label: "App" },
		{
			label: "Key Shouts",
			path: "/app/homepage/key-shouts"
		},
		{ label: "Edit Key Shout" }
	],
	"/observations/forms/edit": [
		{ label: "Observations" },
		{ label: "Forms", path: "/observations/forms" },
		{ label: "Edit Form" }
	],
	"/observations/forms/edit-questions": [
		{ label: "Observations" },
		{ label: "Forms", path: "/observations/forms" },
		{ label: "Edit Questions" }
	],
	"/observations/forms/new": [
		{ label: "Observations" },
		{ label: "Forms", path: "/observations/forms" },
		{ label: "New Form" }
	],
	"/users/people": [{ label: "Users" }, { label: "People" }],
	"/users/people/": [
		{ label: "Users" },
		{ label: "People", path: "/users/people" },
		{ label: "Person" }
	],
	"/users/groups": [{ label: "Users" }, { label: "Groups" }],
	"/users/groups/create": [
		{ label: "Users" },
		{ label: "Groups", path: "/users/groups" },
		{ label: "New Group" }
	],
	"/users/groups/": [
		{ label: "Users" },
		{ label: "Groups", path: "/users/groups" },
		{ label: "Edit Group" }
	],
	"/users/locations": [{ label: "Users" }, { label: "Locations" }],
	"/users/permissions": [{ label: "Users" }, { label: "Permissions" }],
	"/users/permissions/create": [
		{ label: "Users" },
		{ label: "Permissions", path: "/users/permissions" },
		{ label: "New Role" }
	],
	"/users/permissions/": [
		{ label: "Users" },
		{ label: "Permissions", path: "/users/permissions" },
		{ label: "Edit Role" }
	],
	"/users/locations/regions/create": [
		{ label: "Users" },
		{ label: "Locations", path: "/users/locations" },
		{ label: "New Region" }
	],
	"/users/locations/regions/": [
		{ label: "Users" },
		{ label: "Locations", path: "/users/locations" },
		{ label: "Edit Region" }
	],
	"/users/locations/areas/create": [
		{ label: "Users" },
		{ label: "Locations", path: "/users/locations" },
		{ label: "New Area" }
	],
	"/users/locations/areas/": [
		{ label: "Users" },
		{ label: "Locations", path: "/users/locations" },
		{ label: "Edit Area" }
	],
	"/users/locations/stores/create": [
		{ label: "Users" },
		{ label: "Locations", path: "/users/locations" },
		{ label: "New Store" }
	],
	"/users/locations/stores/": [
		{ label: "Users" },
		{ label: "Locations", path: "/users/locations" },
		{ label: "Edit Store" }
	]
};

function matchBreadcrumbs(pathname: string): BreadcrumbItem[] | null {
	// Exact match first
	if (routeBreadcrumbs[pathname]) return routeBreadcrumbs[pathname];

	// Prefix match (for paths with dynamic segments like /edit/123)
	const match = Object.entries(routeBreadcrumbs)
		.sort(([a], [b]) => b.length - a.length)
		.find(([path]) => pathname.startsWith(path));

	return match ? match[1] : null;
}

export function Breadcrumbs() {
	const { pathname } = useLocation();
	const items = matchBreadcrumbs(pathname);

	if (!items) return null;

	return (
		<nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-1">
			<ol className="flex items-center gap-1.5">
				{items.map((item, index) => (
					<li key={item.label} className="flex items-center gap-1.5">
						{index > 0 && (
							<ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
						)}
						{item.path ? (
							<Link
								to={item.path}
								className="hover:text-foreground transition-colors duration-150"
							>
								{item.label}
							</Link>
						) : (
							<span
								className={cn(
									index === items.length - 1 && "text-foreground font-medium"
								)}
								{...(index === items.length - 1
									? { "aria-current": "page" as const }
									: {})}
							>
								{item.label}
							</span>
						)}
					</li>
				))}
			</ol>
		</nav>
	);
}
